import { refreshToken } from '@module/apis/client-side/chat';
import catchAllErrors from '@module/utils/catchAllErrors';
import decodeToken from '@module/utils/decodeToken';
import Centrifuge from 'centrifuge';

type CentrifugeOptions = NonNullable<ConstructorParameters<typeof Centrifuge>[1]>;
type RefreshHandler = NonNullable<CentrifugeOptions['onRefresh']>;

interface RefreshHandlerDeps {
  getAuthContext: () => {
    token: string;
    channel: string;
    endpoint?: string;
  };
  onTokenRefreshed?: (token: string) => void;
}

export const createCentrifugeRefreshHandler = ({
  getAuthContext,
  onTokenRefreshed,
}: RefreshHandlerDeps): RefreshHandler => {
  return async (_context, callback) => {
    const { token: savedToken, channel, endpoint } = getAuthContext();

    if (!savedToken || !channel) {
      callback({
        status: 300,
        data: { token: '' },
      });
      return;
    }

    try {
      const result = await refreshToken({ channel, token: savedToken, endpoint });
      const { token } = result;
      if (result.result && token) {
        const refreshData = {
          status: 200,
          data: {
            token,
          },
        };
        onTokenRefreshed?.(token);
        callback(refreshData);
      }
    } catch (error) {
      catchAllErrors(error);
    }
  };
};

export const getRandomRefreshInterval = () => {
  return Math.floor(Math.random() * (8000 - 2000 + 1)) + 2000;
};

type RefreshConfig = {
  handler: RefreshHandler;
};

interface InitCentrifugeOptions {
  url: string;
  refresh?: RefreshConfig | null;
}

export const initCentrifuge = ({ url, refresh }: InitCentrifugeOptions) => {
  const centrifugeOptions: CentrifugeOptions = {};

  if (refresh) {
    centrifugeOptions.onRefresh = refresh.handler;
    centrifugeOptions.refreshInterval = getRandomRefreshInterval();
    centrifugeOptions.debug = false;
  }

  return new Centrifuge(url, centrifugeOptions);
};

export const disconnectCentrifuge = (centrifuge: Centrifuge | null, subscription: Centrifuge.Subscription | null) => {
  centrifuge?.disconnect();
  subscription?.unsubscribe();
};

export const handleDisconnect = (ctx: { reason: string; reconnect: boolean }) => {
  const { reason } = ctx;
  if (reason === 'invalid token') {
    // TODO 로그아웃 시
  }
};

interface ChatTokenPayload {
  sub: string;
  exp: number;
  info: {
    channel: string;
    type: string;
    dt: string;
    pf: string;
    iat: number;
    ip: string;
  };
  etc: any[];
}

export default function getPayloadFromToken(token: string): ChatTokenPayload {
  const info = decodeToken<ChatTokenPayload>(token);
  return info;
}
